import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Activity, Users, Clock, Bot, AlertTriangle, Wifi, WifiOff, Globe,
  Phone, Zap, Shield, MessageSquare, ArrowRight, RefreshCw, CheckCircle, Timer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/atendimento`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('recupnet_token')}` });

const HEALTH_COLOR = { green: 'rgba(99,203,182,0.15)', yellow: 'rgba(249,168,37,0.12)', red: 'rgba(211,47,47,0.12)' };
const HEALTH_BORDER = { green: 'rgba(99,203,182,0.3)', yellow: 'rgba(249,168,37,0.3)', red: 'rgba(211,47,47,0.3)' };
const HEALTH_DOT = { green: 'var(--rn-green)', yellow: 'var(--rn-warning)', red: '#D32F2F' };

const EVENT_LABELS = {
  conversation_created: 'Novo ticket', message_customer: 'Msg cliente', message_operator: 'Msg operador',
  message_ai: 'IA respondeu', transfer_human: 'Handoff humano', transfer_ai: 'Retorno IA',
  operator_assigned: 'Operador atribuido', closed: 'Encerrado', classified: 'Classificado',
  routed: 'Roteado', sla_paused: 'SLA pausado', handoff_human: 'Handoff',
};

export default function AtendimentoCommandCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/command-center`, { headers: headers() });
      setData(r.data);
      setLastUpdate(new Date());
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    pollRef.current = setInterval(fetchData, 8000);
    return () => clearInterval(pollRef.current);
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="rn-spinner" style={{ width: 32, height: 32 }} /></div>;

  const k = data?.kpis || {};

  return (
    <div className="p-5 space-y-5 overflow-y-auto" style={{ maxHeight: '100vh' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity size={22} style={{ color: 'var(--rn-green)' }} />
          <div>
            <h1 className="font-display text-xl font-semibold">Sala de Comando</h1>
            <p className="text-[10px] text-white/30">Monitoramento operacional em tempo real</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && <span className="text-[9px] text-white/20">Atualizado {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
          <button onClick={fetchData} className="p-1.5 rounded-lg hover:bg-white/5"><RefreshCw size={14} className="text-white/30" /></button>
        </div>
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
        <KPI icon={MessageSquare} label="Ativos" value={k.active} />
        <KPI icon={Zap} label="Novos hoje" value={k.newToday} color="green" />
        <KPI icon={Clock} label="Fila humano" value={k.waitingHuman} color={k.waitingHuman > 3 ? 'red' : 'warning'} />
        <KPI icon={Timer} label="Aguard. cliente" value={k.waitingClient} />
        <KPI icon={AlertTriangle} label="SLA risco" value={k.slaRisk} color={k.slaRisk > 0 ? 'warning' : ''} />
        <KPI icon={Shield} label="SLA violado" value={k.slaViolated} color={k.slaViolated > 0 ? 'red' : ''} />
        <KPI icon={Users} label="Operadores" value={`${k.opsOnline}/${k.opsTotal}`} color="green" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* === COL 1: Queues + SLA === */}
        <div className="space-y-4">
          {/* Queues */}
          <Panel title="Filas" icon={Activity}>
            {data?.queues?.map(q => (
              <div key={q.id} className="flex items-center gap-2 p-2 rounded-lg mb-1.5"
                style={{ background: HEALTH_COLOR[q.health], border: `1px solid ${HEALTH_BORDER[q.health]}` }}>
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: HEALTH_DOT[q.health] }} />
                <span className="text-xs text-white/80 flex-1">{q.name}</span>
                <span className="text-[9px] text-white/40">{q.waiting} fila</span>
                <span className="text-[9px] text-white/40">{q.active} ativos</span>
              </div>
            ))}
          </Panel>

          {/* SLA Monitor */}
          <Panel title="SLA Criticos" icon={Shield}>
            {!data?.slaTickets?.length ? (
              <p className="text-[10px] text-white/25 text-center py-3">Nenhum SLA critico</p>
            ) : data.slaTickets.map(t => (
              <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg mb-1.5 cursor-pointer hover:bg-white/[0.03]"
                style={{ background: t.slaStatus === 'violated' ? 'rgba(211,47,47,0.08)' : 'rgba(249,168,37,0.06)', border: `1px solid ${t.slaStatus === 'violated' ? 'rgba(211,47,47,0.2)' : 'rgba(249,168,37,0.15)'}` }}
                onClick={() => navigate(`/admin/atendimento/ticket/${t.id}`)}>
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${t.slaStatus === 'violated' ? 'badge-danger' : 'badge-warning'}`}>
                  {t.slaStatus === 'violated' ? 'VIOLADO' : 'RISCO'}
                </span>
                <span className="text-[10px] text-white/60 flex-1 truncate">{t.contactName}</span>
                <span className="text-[9px] text-white/30">{t.elapsedMin}min</span>
              </div>
            ))}
          </Panel>
        </div>

        {/* === COL 2: Operators + AI === */}
        <div className="space-y-4">
          {/* Operators */}
          <Panel title="Operadores" icon={Users}>
            {data?.operators?.map(op => (
              <div key={op.id} className="flex items-center gap-2 p-2 rounded-lg mb-1.5 bg-white/[0.02]">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${op.status === 'online' ? 'bg-[var(--rn-green)]' : op.status === 'busy' ? 'bg-[var(--rn-warning)]' : 'bg-white/20'}`} />
                <span className="text-[10px] text-white/80 flex-1">{op.name}</span>
                {/* Load bar */}
                <div className="w-14 h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${op.load}%`, background: op.load > 80 ? '#D32F2F' : op.load > 50 ? 'var(--rn-warning)' : 'var(--rn-green)' }} />
                </div>
                <span className="text-[9px] text-white/30 w-8 text-right">{op.active}/{op.max}</span>
              </div>
            ))}
          </Panel>

          {/* AI */}
          <Panel title="Inteligencia Artificial" icon={Bot}>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <MiniKPI label="Resolvidos IA" value={`${data?.ai?.resolvedRate || 0}%`} color="green" />
              <MiniKPI label="Handoffs" value={data?.ai?.handoffs || 0} color={data?.ai?.handoffs > 0 ? 'warning' : ''} />
            </div>
            {data?.categories?.length > 0 && (
              <div>
                <p className="text-[9px] text-white/30 mb-1">Categorias detectadas</p>
                <div className="flex flex-wrap gap-1">
                  {data.categories.map(c => (
                    <span key={c.name} className="text-[8px] px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/10 text-white/50">{c.name} <b className="text-white/70">{c.count}</b></span>
                  ))}
                </div>
              </div>
            )}
          </Panel>
        </div>

        {/* === COL 3: Channels + Feed === */}
        <div className="space-y-4">
          {/* Channels */}
          <Panel title="Canais" icon={Globe}>
            <div className="space-y-2">
              <ChannelRow icon={Phone} name="WhatsApp" active={data?.channels?.whatsapp?.active}
                today={data?.channels?.whatsapp?.today} status={data?.channels?.whatsapp?.status} />
              <ChannelRow icon={Globe} name="Webchat" active={data?.channels?.webchat?.active}
                today={data?.channels?.webchat?.today} status={data?.channels?.webchat?.status} />
            </div>
          </Panel>

          {/* Feed */}
          <Panel title="Feed Operacional" icon={Activity}>
            <div className="space-y-1 max-h-[260px] overflow-y-auto">
              {data?.feed?.map((ev, i) => (
                <div key={i} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-white/[0.02] cursor-pointer"
                  onClick={() => ev.conversationId && navigate(`/admin/atendimento/ticket/${ev.conversationId}`)}>
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: 'var(--rn-green)' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-white/60">
                      <span className="text-white/40">{EVENT_LABELS[ev.eventType] || ev.eventType}</span>
                      {ev.contactName && <span className="text-white/70"> — {ev.contactName}</span>}
                    </p>
                    <p className="text-[8px] text-white/20">{ev.createdAt ? new Date(ev.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  </div>
                </div>
              ))}
              {(!data?.feed || !data.feed.length) && <p className="text-[10px] text-white/20 text-center py-4">Sem eventos recentes</p>}
            </div>
          </Panel>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: 'Inbox', path: '/admin/atendimento/inbox' },
          { label: 'Tickets criticos', path: '/admin/atendimento/inbox?status=waiting_human' },
          { label: 'Sem operador', path: '/admin/atendimento/inbox?owner=unassigned' },
          { label: 'Analytics IA', path: '/admin/atendimento' },
          { label: 'WhatsApp', path: '/admin/atendimento/whatsapp' },
          { label: 'Webchat', path: '/admin/atendimento/webchat' },
          { label: 'Base de Conhecimento', path: '/admin/atendimento/knowledge' },
        ].map(a => (
          <button key={a.label} onClick={() => navigate(a.path)}
            className="text-[10px] px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-white/40 hover:text-white/70 hover:border-[rgba(99,203,182,0.2)] transition-colors flex items-center gap-1">
            {a.label} <ArrowRight size={10} />
          </button>
        ))}
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl p-3.5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(99,203,182,0.08)', backdropFilter: 'blur(8px)' }}>
      <h3 className="text-[11px] font-semibold text-white/50 mb-2.5 flex items-center gap-1.5">
        <Icon size={13} style={{ color: 'var(--rn-green)' }} /> {title}
      </h3>
      {children}
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  const c = color === 'green' ? 'var(--rn-green)' : color === 'warning' ? 'var(--rn-warning)' : color === 'red' ? '#D32F2F' : 'rgba(255,255,255,0.3)';
  return (
    <div className="rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <Icon size={14} style={{ color: c }} className="mb-1" />
      <p className="font-display text-lg font-bold tabular-nums">{value ?? 0}</p>
      <p className="text-[9px] text-white/35">{label}</p>
    </div>
  );
}

function MiniKPI({ label, value, color }) {
  const c = color === 'green' ? 'var(--rn-green)' : color === 'warning' ? 'var(--rn-warning)' : 'rgba(255,255,255,0.5)';
  return (
    <div className="rounded-lg p-2 text-center" style={{ background: 'rgba(255,255,255,0.02)' }}>
      <p className="font-display text-base font-bold" style={{ color: c }}>{value}</p>
      <p className="text-[8px] text-white/30">{label}</p>
    </div>
  );
}

function ChannelRow({ icon: Icon, name, active, today, status }) {
  const connected = status === 'connected' || status === 'active';
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02]">
      {connected ? <Wifi size={13} style={{ color: 'var(--rn-green)' }} /> : <WifiOff size={13} className="text-red-400" />}
      <span className="text-[10px] text-white/70 flex-1">{name}</span>
      <span className="text-[9px] text-white/30">{active || 0} ativos</span>
      <span className="text-[9px] text-white/30">{today || 0} hoje</span>
    </div>
  );
}
